// Main calendar view — react-big-calendar with the family's events.
import { useEffect, useMemo, useState } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer, type View } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { RRule } from "rrule";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { api, type CalendarEvent as InklingEvent } from "../lib/api.js";
import { useFamily } from "../lib/family.js";
import { HeaderNav } from "../components/HeaderNav.js";

// Reactive viewport-size hook used to pick a sensible default view (day vs
// week) on phones. The week-view is too cramped at <640px to be usable —
// users get a headache toggling between events. Day view fits.
function useIsNarrow(maxWidthPx = 640): boolean {
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${maxWidthPx}px)`);
    const onChange = () => setIsNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [maxWidthPx]);
  return isNarrow;
}

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

interface RBCEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: InklingEvent;
}

export function CalendarPage() {
  const { family } = useFamily();
  const isNarrow = useIsNarrow();
  // Phones default to 'day' (week-view at <640px is unreadable). Once
  // the user picks a view explicitly we keep it — don't whiplash the
  // selection on rotation.
  const [view, setView] = useState<View>(() => (isNarrow ? "day" : "week"));
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState<RBCEvent[]>([]);
  const [selected, setSelected] = useState<InklingEvent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => visibleRange(view, date), [view, date]);

  useEffect(() => {
    if (!family) return;
    (async () => {
      try {
        const { events } = await api.listEvents(family.id, range.from, range.to);
        setEvents(events.flatMap((e) => expandToRbc(e, range.from, range.to)));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load events");
      }
    })();
  }, [family?.id, range.from.getTime(), range.to.getTime()]);

  if (!family) {
    return <div className="p-8 text-stone-600">Loading family…</div>;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">{family.name} family</h1>
            <p className="text-sm text-stone-500">{family.twilioPhoneNumber}</p>
          </div>
          <HeaderNav active="calendar" />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-6">
        {error && <p className="text-red-600 mb-4">{error}</p>}
        {events.length === 0 ? (
          <EmptyState
            twilioPhoneNumber={family.twilioPhoneNumber}
            familyName={family.name}
          />
        ) : null}
        <div
          className={`bg-white rounded-xl border border-stone-200 p-4 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 ${
            events.length === 0 ? "mt-6" : ""
          }`}
          style={{ height: "70vh" }}
          tabIndex={0}
          aria-label="Family calendar — use arrow keys to navigate"
        >
          <BigCalendar
            localizer={localizer}
            events={events}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            views={isNarrow ? ["day", "month"] : ["day", "week", "month"]}
            defaultView={isNarrow ? "day" : "week"}
            popup
            step={isNarrow ? 60 : 30}
            timeslots={isNarrow ? 1 : 2}
            scrollToTime={scrollAnchor()}
            tooltipAccessor={(e) => (e as RBCEvent).title}
            onSelectEvent={(e) => setSelected((e as RBCEvent).resource)}
          />
        </div>
      </main>

      {selected && (
        <EventDrawer
          event={selected}
          onClose={() => setSelected(null)}
          onMutated={async () => {
            const { events } = await api.listEvents(family.id, range.from, range.to);
            setEvents(events.flatMap((ev) => expandToRbc(ev, range.from, range.to)));
          }}
        />
      )}
    </div>
  );
}

function EventDrawer({
  event,
  onClose,
  onMutated,
}: {
  event: InklingEvent;
  onClose: () => void;
  onMutated: () => Promise<void>;
}) {
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [draft, setDraft] = useState(() => toDraft(event));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset draft when the focused event changes (e.g. user clicks a different
  // event without closing the drawer).
  useEffect(() => {
    setDraft(toDraft(event));
    setMode("view");
    setError(null);
  }, [event.id]);

  async function onSave() {
    setBusy(true);
    setError(null);
    try {
      const patch: Parameters<typeof api.patchEvent>[1] = {};
      if (draft.title !== event.title) patch.title = draft.title.trim();
      if (draft.location !== (event.location ?? "")) {
        patch.location = draft.location.trim() || null;
      }
      const startIso = localToIso(draft.startLocal);
      if (startIso && startIso !== event.startTime) patch.startTime = startIso;
      const endIso = draft.endLocal ? localToIso(draft.endLocal) : null;
      const eventEndIso = event.endTime ?? null;
      if (endIso !== eventEndIso) patch.endTime = endIso;
      if (draft.recurrence !== (event.recurrenceRrule ?? "")) {
        patch.recurrenceRrule = draft.recurrence.trim() || null;
      }
      if (Object.keys(patch).length === 0) {
        setMode("view");
        setBusy(false);
        return;
      }
      await api.patchEvent(event.id, patch);
      await onMutated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!window.confirm(`Delete "${event.title}"? This cancels it on every connected calendar.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.deleteEvent(event.id);
      await onMutated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {mode === "view" ? (
          <>
            <h2 className="text-xl font-semibold">{event.title}</h2>
            <dl className="mt-3 space-y-1 text-sm">
              <Row label="When">{formatEventWhen(event.startTime, event.endTime)}</Row>
              {event.location && <Row label="Where">{event.location}</Row>}
              {event.recurrenceRrule && <Row label="Repeats">{event.recurrenceRrule}</Row>}
              {event.attendees.length > 0 && <Row label="Who">{event.attendees.join(", ")}</Row>}
              {event.confidenceScore !== null && (
                <Row label="Source confidence">{Math.round(event.confidenceScore * 100)}%</Row>
              )}
              <Row label="Status">{event.status}</Row>
            </dl>

            {event.sourceMessageId && <SourceMedia eventId={event.id} />}

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setMode("edit")}
                className="rounded-lg bg-stone-900 text-white px-5 py-2 font-medium hover:bg-stone-800"
              >
                Edit
              </button>
              <button
                onClick={onDelete}
                disabled={busy}
                className="rounded-lg border border-red-300 text-red-700 px-5 py-2 font-medium hover:bg-red-50 disabled:opacity-50"
              >
                {busy ? "Deleting…" : "Delete"}
              </button>
              <button onClick={onClose} className="rounded-lg border border-stone-300 px-5 py-2 font-medium hover:bg-stone-50 sm:ml-auto">
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold">Edit event</h2>
            <div className="mt-4 space-y-3 text-sm">
              <Field label="Title">
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  className="w-full rounded-md border border-stone-300 px-3 py-2"
                />
              </Field>
              <Field label="Starts">
                <input
                  type="datetime-local"
                  value={draft.startLocal}
                  onChange={(e) => setDraft((d) => ({ ...d, startLocal: e.target.value }))}
                  className="w-full rounded-md border border-stone-300 px-3 py-2"
                />
              </Field>
              <Field label="Ends">
                <input
                  type="datetime-local"
                  value={draft.endLocal}
                  onChange={(e) => setDraft((d) => ({ ...d, endLocal: e.target.value }))}
                  placeholder="(optional)"
                  className="w-full rounded-md border border-stone-300 px-3 py-2"
                />
              </Field>
              <Field label="Location">
                <input
                  type="text"
                  value={draft.location}
                  onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
                  className="w-full rounded-md border border-stone-300 px-3 py-2"
                />
              </Field>
              <Field label="Repeats">
                <input
                  type="text"
                  value={draft.recurrence}
                  onChange={(e) => setDraft((d) => ({ ...d, recurrence: e.target.value }))}
                  placeholder="FREQ=WEEKLY;BYDAY=TU (leave blank for one-off)"
                  className="w-full rounded-md border border-stone-300 px-3 py-2 font-mono text-xs"
                />
              </Field>
            </div>

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <button
                onClick={onSave}
                disabled={busy}
                className="rounded-lg bg-stone-900 text-white px-5 py-2 font-medium hover:bg-stone-800 disabled:opacity-50"
              >
                {busy ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => {
                  setMode("view");
                  setDraft(toDraft(event));
                  setError(null);
                }}
                disabled={busy}
                className="rounded-lg border border-stone-300 px-5 py-2 font-medium hover:bg-stone-50"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface DraftEvent {
  title: string;
  location: string;
  startLocal: string; // datetime-local format (YYYY-MM-DDTHH:mm)
  endLocal: string;
  recurrence: string;
}

function toDraft(e: InklingEvent): DraftEvent {
  return {
    title: e.title,
    location: e.location ?? "",
    startLocal: isoToLocal(e.startTime),
    endLocal: e.endTime ? isoToLocal(e.endTime) : "",
    recurrence: e.recurrenceRrule ?? "",
  };
}

/** ISO-8601 string → 'YYYY-MM-DDTHH:mm' in the visitor's local TZ for the
 * datetime-local input. */
function isoToLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local 'YYYY-MM-DDTHH:mm' → ISO-8601 with the visitor's TZ
 * offset baked in. Returns null if the input is empty/unparseable. */
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-stone-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function SourceMedia({ eventId }: { eventId: string }) {
  // Try indices 0..3 — most events have 0 or 1 media; cap at 4 to avoid
  // hammering the API on the rare flyer with many attachments.
  const [items, setItems] = useState<Array<{ idx: number; url: string }>>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out: Array<{ idx: number; url: string }> = [];
      for (let i = 0; i < 4; i++) {
        try {
          const { url } = await api.eventSourceMediaUrl(eventId, i);
          if (cancelled) return;
          out.push({ idx: i, url });
        } catch {
          // 404 = no media at this index; we stop walking.
          break;
        }
      }
      if (!cancelled) setItems(out);
    })().catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load source");
    });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (error) return <p className="mt-3 text-xs text-stone-500">Source media unavailable.</p>;
  if (items.length === 0) return null;

  return (
    <div className="mt-4 border-t border-stone-200 pt-3">
      <p className="text-xs uppercase tracking-widest text-stone-500">Source</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map(({ idx, url }) => (
          <a
            key={idx}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-24 h-24 rounded-lg overflow-hidden border border-stone-200 hover:border-stone-400"
          >
            {/* Images render directly; non-image media (audio/voice notes)
                shows a generic media-link tile. */}
            <img
              src={url}
              alt={`Source media ${idx + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                const t = e.currentTarget;
                t.style.display = "none";
                if (t.parentElement) t.parentElement.textContent = `📎 attachment ${idx + 1}`;
              }}
            />
          </a>
        ))}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-32 shrink-0 text-stone-500">{label}</dt>
      <dd className="text-stone-900">{children}</dd>
    </div>
  );
}

function formatEventWhen(startIso: string, endIso: string | null): string {
  const start = new Date(startIso);
  const dayPart = format(start, "EEE, MMM d");
  const startPart = format(start, "h:mma").toLowerCase();
  if (!endIso) return `${dayPart} · ${startPart}`;
  const end = new Date(endIso);
  if (start.getTime() === end.getTime()) return `${dayPart} · ${startPart}`;
  const sameDay = start.toDateString() === end.toDateString();
  const endPart = format(end, "h:mma").toLowerCase();
  return sameDay
    ? `${dayPart} · ${startPart}–${endPart}`
    : `${dayPart} ${startPart} → ${format(end, "EEE, MMM d")} ${endPart}`;
}

function toRBC(e: InklingEvent): RBCEvent {
  const start = new Date(e.startTime);
  const end = e.endTime ? new Date(e.endTime) : new Date(start.getTime() + 60 * 60 * 1000);
  return { id: e.id, title: e.title, start, end, allDay: false, resource: e };
}

/**
 * Expand an event into one or more visible instances over [from, to).
 *
 * - Non-recurring → a single RBCEvent (existing behavior).
 * - Recurring (recurrenceRrule set) → one RBCEvent per occurrence inside
 *   the visible window. Each occurrence carries the same title and a
 *   stable id of `${parent.id}#${occurrenceMs}` so RBC can dedupe and the
 *   drawer click maps back to the parent row's `resource`.
 *
 * Bounded expansion via rrule.js's `between(from, to)` so we never
 * materialize "every day forever" into infinity. If the rule body is
 * malformed (Claude occasionally hallucinates), we fall back to the
 * single-instance behavior so the parent event still renders.
 */
function expandToRbc(e: InklingEvent, from: Date, to: Date): RBCEvent[] {
  if (!e.recurrenceRrule) return [toRBC(e)];
  const start = new Date(e.startTime);
  const durationMs = e.endTime
    ? new Date(e.endTime).getTime() - start.getTime()
    : 60 * 60 * 1000;
  let rule: RRule;
  try {
    rule = RRule.fromString(`DTSTART:${formatRruleDate(start)}\nRRULE:${e.recurrenceRrule}`);
  } catch {
    return [toRBC(e)];
  }
  // between() is inclusive on both ends; pad `to` by 1ms to keep the
  // visible-range half-open semantics consistent with the API query.
  const occurrences = rule.between(from, new Date(to.getTime() + 1), true);
  if (occurrences.length === 0) return [];
  return occurrences.map((occStart) => ({
    id: `${e.id}#${occStart.getTime()}`,
    title: e.title,
    start: occStart,
    end: new Date(occStart.getTime() + durationMs),
    allDay: false,
    resource: e,
  }));
}

/** rrule.js wants DTSTART in UTC basic-format (YYYYMMDDTHHMMSSZ). */
function formatRruleDate(d: Date): string {
  const y = d.getUTCFullYear().toString().padStart(4, "0");
  const m = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  const h = d.getUTCHours().toString().padStart(2, "0");
  const mi = d.getUTCMinutes().toString().padStart(2, "0");
  const s = d.getUTCSeconds().toString().padStart(2, "0");
  return `${y}${m}${day}T${h}${mi}${s}Z`;
}

/** Anchor week/day view to the user's current local time, two hours before
 * "now" so the next thing on their schedule is visible without scrolling. */
function scrollAnchor(): Date {
  const d = new Date();
  d.setHours(Math.max(0, d.getHours() - 2), 0, 0, 0);
  return d;
}

function EmptyState({
  twilioPhoneNumber,
  familyName,
}: {
  twilioPhoneNumber: string;
  familyName: string;
}) {
  const [copied, setCopied] = useState(false);
  const pretty = prettyPhone(twilioPhoneNumber);
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-8 text-center shadow-sm">
      <h2 className="text-xl font-semibold tracking-tight">
        Welcome to your {familyName} family calendar
      </h2>
      <p className="mt-2 text-stone-600">
        Nothing here yet — text your family number to add the first event.
      </p>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(twilioPhoneNumber);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            // ignore — fallback is the visible number itself
          }
        }}
        className="mt-5 inline-flex items-center gap-2 rounded-lg bg-stone-100 px-4 py-3 font-mono text-lg hover:bg-stone-200 transition"
        aria-label={`Copy ${pretty} to clipboard`}
      >
        <span>{pretty}</span>
        <span className="text-xs text-stone-500">{copied ? "copied" : "tap to copy"}</span>
      </button>
      <p className="mt-4 text-sm text-stone-500">
        Try: <em>"Soccer practice tomorrow at 4pm at the park"</em>, a
        photo of a school flyer, or a voice note.
      </p>
    </div>
  );
}

function prettyPhone(p: string): string {
  const m = p.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `(${m[1]}) ${m[2]}-${m[3]}`;
  return p;
}

function visibleRange(view: View, date: Date): { from: Date; to: Date } {
  const d = new Date(date);
  if (view === "day") {
    const from = new Date(d); from.setHours(0, 0, 0, 0);
    const to = new Date(d); to.setHours(23, 59, 59, 999);
    return { from, to };
  }
  if (view === "month") {
    const from = new Date(d.getFullYear(), d.getMonth(), 1);
    const to = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    return { from, to };
  }
  const from = startOfWeek(d, { locale: enUS });
  const to = new Date(from); to.setDate(to.getDate() + 7);
  return { from, to };
}
